import { styled } from '@mui/material/styles';
import { SearchInput } from './SearchInput';
import { SelectInput } from './SelectInput';
import { AdvisorConsoleStoreFilters } from 'features/advisorConsole/types';
import { getStatusOptions, getRiskProfileOptions } from '../../../common/constants';
import { OptionType } from './Dropdown';
import { useAppSelector, useIsMultiPortfolioTenant } from '../../../common/hooks';
import { useTranslation } from 'react-i18next';
import { getClientJourneyFlowType, isTenantAdmin } from '../../../utils';

type Props = {
    filters: AdvisorConsoleStoreFilters;
    // eslint-disable-next-line
    onChangeFilters(filters: AdvisorConsoleStoreFilters): void;
    clients: OptionType<string>[];
};

export const AdminFilters = ({ filters, onChangeFilters, clients }: Props) => {
    const { t } = useTranslation();
    const global = useAppSelector((state) => state.global);
    const user = useAppSelector((state) => state.auth?.user);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const { status, riskProfile, client, advisor } = filters || {};
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    const statusOptions = getStatusOptions(t, isFlow2);
    const riskProfiles = global?.goeConfig?.riskProfiles || [];
    const advisors = useAppSelector((state) => state?.advisorAdmin?.advisors);
    const riskProfileOptions = riskProfiles?.length
        ? [
              {
                  value: undefined,
                  label: t('ALL_TEXT')
              },
              ...riskProfiles.map((profile) => ({
                  label: t(`${profile.toUpperCase()}`),
                  value: profile
              }))
          ]
        : getRiskProfileOptions(t);
    // Track usernames already added (starting with the logged-in user) so the advisors list
    // doesn't repeat the current user or duplicate entries.
    const seenAdvisorNames = new Set<string | undefined>([user?.loggedInUsername]);
    const advisorOptions: OptionType<string | undefined>[] = [
        {
            value: undefined,
            label: t('ALL_TEXT')
        },
        {
            value: user?.loggedInUsername,
            label: user?.loggedInUsername
        },
        ...(advisors || [])
            .filter((advisor) => {
                const name = advisor?.userName;
                if (!name || seenAdvisorNames.has(name)) return false;
                seenAdvisorNames.add(name);
                return true;
            })
            .map((advisor) => ({
                value: advisor?.userName,
                label: advisor?.userName || t(advisor?.userName?.toUpperCase())
            }))
    ];

    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();

    return (
        <Container>
            <div
                style={{
                    minWidth: '200px',
                    width: '560px',
                    maxWidth: '100%',
                    marginRight: '17px',
                    marginBottom: 'auto'
                }}
            >
                <SearchInput
                    value={client}
                    onChange={(value) => onChangeFilters({ ...filters, client: value })}
                    clients={clients}
                />
            </div>

            <SelectsContainer>
                <SelectInput
                    options={statusOptions}
                    label={t('NEW_ADVISOR_CONSOLE_STATUS')}
                    name="filter-status"
                    value={status}
                    onChange={(value) => onChangeFilters({ ...filters, status: value })}
                />

                {!isFlow2 && !isMultiPortfolioTenant && (
                    <SelectInput
                        options={riskProfileOptions}
                        label={t('LABEL_RISK_PROFILE')}
                        name="filter-risk-profile"
                        value={riskProfile}
                        onChange={(value) => onChangeFilters({ ...filters, riskProfile: value })}
                    />
                )}
                {isTenantAdmin(user) && (
                    <SelectInput
                        options={advisorOptions}
                        label={t('ADVISOR_TEXT')}
                        name="filter-advisor"
                        value={advisor}
                        maxHeight={300}
                        onChange={(value) => onChangeFilters({ ...filters, advisor: value })}
                    />
                )}
            </SelectsContainer>
        </Container>
    );
};

const Container = styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: '12px',
    marginBottom: '24px',
    overflowX: 'auto',
    justifyContent: 'space-between'
}));

const SelectsContainer = styled('div')(() => ({
    display: 'flex',
    gap: '8px'
}));
